import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './SelfCare.module.css'

const AFFIRMATIONS = [
  "You are the parent your child needs. Not perfect — present.",
  "It's okay to feel overwhelmed. It means you care deeply.",
  "Your patience today is shaping who they become tomorrow.",
  "No one knows your child better than you do.",
  "A calm response in chaos teaches more than a thousand lectures.",
  "You don't have to enjoy every moment to be a good parent.",
  "Asking for help is strength, not weakness.",
  "Your child doesn't need a perfect parent. They need a real one.",
  "The fact that you're trying makes you an amazing parent.",
  "Some days surviving IS thriving. And that's enough.",
  "You are more than just a parent. Your needs matter too.",
  "Progress, not perfection. For both you and your child.",
  "Yelling doesn't make you a bad parent. Coming back with love makes you a great one.",
  "Your child's difficult day is not a reflection of your parenting.",
  "Rest is not a reward. It's a requirement.",
  "You are learning and growing alongside your child.",
  "The house can wait. The laundry can wait. Your child won't be little forever.",
  "Comparison is the thief of parenting joy. Your journey is unique.",
  "Taking 5 minutes for yourself today makes you a better parent tomorrow.",
  "You're not raising a child. You're raising a future adult. And you're doing it beautifully.",
]

const EXERCISES = [
  { emoji: '', title: 'Cold water reset', desc: 'Run cold water on your wrists for 30 seconds. It activates your dive reflex and instantly lowers heart rate.' },
  { emoji: '', title: '5-4-3-2-1 grounding', desc: 'Name 5 things you see, 4 you can touch, 3 you hear, 2 you smell, 1 you taste.' },
  { emoji: '', title: "Lion's breath", desc: 'Big inhale through nose, open mouth wide, stick tongue out, exhale with a "haaa". Release jaw tension.' },
  { emoji: '', title: 'Hand massage', desc: 'Press your thumb firmly into your opposite palm, making slow circles. 1 minute per hand.' },
  { emoji: '', title: 'Hum a tune', desc: 'Humming activates your vagus nerve and calms your nervous system. Hum for 1 minute.' },
  { emoji: '', title: 'Ragdoll shake', desc: 'Stand up, shake your hands, arms, legs, whole body for 30 seconds. Releases stress hormones.' },
]

const REMINDERS = [
  "Their meltdown is not your failure",
  "You were a person before you were a parent",
  "This phase will pass",
  "You don't need to fix everything today",
]

const REMINDER_COLORS = ['#E8F5E9', '#FFF3E0', '#E3F2FD', '#FFF9C4']

function getDailyAffirmation(): string {
  const d = new Date(); const dayOfYear = Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 0).getTime()) / 86400000)
  return AFFIRMATIONS[dayOfYear % AFFIRMATIONS.length]
}

export default function SelfCare() {
  const navigate = useNavigate()
  const [breathing, setBreathing] = useState(false)
  const [breathPhase, setBreathPhase] = useState<'in' | 'out'>('in')
  const [breathCount, setBreathCount] = useState(0)
  const [breathDone, setBreathDone] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  const stopBreathing = useCallback(() => {
    setBreathing(false); setBreathDone(true)
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  useEffect(() => {
    if (!breathing) return
    const cycle = () => {
      setBreathPhase('in')
      timerRef.current = setTimeout(() => {
        setBreathPhase('out')
        timerRef.current = setTimeout(() => {
          setBreathCount(prev => {
            if (prev + 1 >= 5) { stopBreathing(); return 5 }
            cycle(); return prev + 1
          })
        }, 6000)
      }, 4000)
    }
    cycle()
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [breathing, stopBreathing])

  const startBreathing = () => { setBreathCount(0); setBreathDone(false); setBreathing(true); setBreathPhase('in') }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <h1 className={styles.title}>Your Calm Corner</h1>
      </header>

      <div className={styles.hero}>
        <p>You can't pour from an empty cup. Taking care of yourself isn't selfish — it's necessary.</p>
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Quick Breathe</h2>
        {!breathing && !breathDone && (
          <button className={styles.breatheBtn} onClick={startBreathing}>Take 5 breaths</button>
        )}
        {breathing && (
          <div className={styles.breatheArea}>
            <div className={`${styles.breatheCircle} ${breathPhase === 'in' ? styles.breatheIn : styles.breatheOut}`} />
            <p className={styles.breatheLabel}>{breathPhase === 'in' ? 'Breathe in...' : 'Breathe out...'}</p>
            <p className={styles.breatheCount}>{breathCount + 1} of 5</p>
          </div>
        )}
        {breathDone && (
          <div className={styles.breatheDone}>
            <p>Well done. How do you feel?</p>
            <button className={styles.breatheBtn} onClick={startBreathing}>Again</button>
          </div>
        )}
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Today's Affirmation</h2>
        <div className={styles.affirmationCard}>
          <span className={styles.quote}>"</span>
          <p className={styles.affirmationText}>{getDailyAffirmation()}</p>
          <button className={styles.shareBtn} onClick={() => navigator.clipboard.writeText(getDailyAffirmation())}>Copy</button>
        </div>
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Quick Reset Exercises</h2>
        <div className={styles.exercises}>
          {EXERCISES.map(ex => (
            <div key={ex.title} className={styles.exerciseCard}>
              <span className={styles.exerciseEmoji}>{ex.emoji}</span>
              <div className={styles.exerciseText}>
                <h3 className={styles.exerciseTitle}>{ex.title}</h3>
                <p className={styles.exerciseDesc}>{ex.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Remind Yourself</h2>
        <div className={styles.reminders}>
          {REMINDERS.map((r, i) => (
            <div key={i} className={styles.reminderCard} style={{ backgroundColor: REMINDER_COLORS[i] }}>
              <p>{r}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
